/**
 * PATH: apps/hr/src/pages/leave/LeavePage.tsx
 * PURPOSE: Merged Leave & Attendance management page (orchestrator)
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ StatCards     │───▶│ Tab Toggle   │───▶│ Table View   │
 *   └──────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - components/types: API types + helpers
 *   - components/StatCards: Stat card display
 *   - components/LeaveRequestsTable: Leave requests with approve/reject
 *   - components/TimesheetsTable: Timesheet days display
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarOff, Clock, Search, List, Plus, AlertCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import { hrApi } from '../../lib/setupApi'
import { StatCard } from './components/StatCards'
import { LeaveRequestsTable } from './components/LeaveRequestsTable'
import { TimesheetsTable } from './components/TimesheetsTable'
import {
  type LeaveStatusFilter,
  type TimesheetStatusFilter,
  type LeaveRequestsResponse,
  type TimesheetDaysResponse,
  type TimesheetStatsApi,
  isDateInRangeInclusive,
} from './components/types'

export default function LeavePage() {
  const queryClient = useQueryClient()
  const [mainTab, setMainTab] = useState<'leave' | 'timesheets'>('leave')
  const [leaveFilter, setLeaveFilter] = useState<LeaveStatusFilter>('all')
  const [timesheetFilter, setTimesheetFilter] = useState<TimesheetStatusFilter>('all')
  const [search, setSearch] = useState('')

  const today = useMemo(() => new Date(), [])
  const { fromDateISO, toDateISO } = useMemo(() => {
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - 30)
    return { fromDateISO: from.toISOString().slice(0, 10), toDateISO: to.toISOString().slice(0, 10) }
  }, [])

  const { data: leaveRes, isLoading: isLeaveLoading, error: leaveError } = useQuery({
    queryKey: ['hr-leave-requests'],
    queryFn: async () => { const res = await hrApi.getLeaveRequests(); return res.data as LeaveRequestsResponse },
    staleTime: 15_000, retry: 1,
  })

  const { data: timesheetStats } = useQuery({
    queryKey: ['hr-timesheet-stats'],
    queryFn: async () => { const res = await hrApi.getTimesheetStats(); return res.data as TimesheetStatsApi },
    staleTime: 15_000, retry: 1,
  })

  const { data: timesheetDaysRes, isLoading: isTimesheetsLoading, error: timesheetsError } = useQuery({
    queryKey: ['hr-timesheet-days', fromDateISO, toDateISO, timesheetFilter],
    queryFn: async () => {
      const res = await hrApi.getTimesheetDays({
        from_date: fromDateISO, to_date: toDateISO,
        status: timesheetFilter === 'all' ? undefined : timesheetFilter,
      })
      return res.data as TimesheetDaysResponse
    },
    staleTime: 15_000, retry: 1,
  })

  const leaveRequests = useMemo(() => leaveRes?.requests ?? [], [leaveRes?.requests])
  const pendingLeaveCount = useMemo(() => leaveRequests.filter(r => r.status === 'pending').length, [leaveRequests])
  const onLeaveTodayCount = useMemo(
    () => leaveRequests.filter(r => r.status === 'approved' && isDateInRangeInclusive(today, r.start_date, r.end_date)).length,
    [leaveRequests, today],
  )

  const filteredLeave = useMemo(() => {
    let data = [...leaveRequests]
    if (leaveFilter !== 'all') data = data.filter(r => r.status === leaveFilter)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      data = data.filter(r => r.worker_name.toLowerCase().includes(q) || r.leave_type_name.toLowerCase().includes(q))
    }
    return data
  }, [leaveRequests, leaveFilter, search])

  const filteredTimesheets = useMemo(() => {
    const days = timesheetDaysRes?.days || []
    if (!search.trim()) return days
    const q = search.trim().toLowerCase()
    return days.filter(d => d.worker_name.toLowerCase().includes(q))
  }, [timesheetDaysRes?.days, search])

  const approveMutation = useMutation({
    mutationFn: async (requestId: string) => { await hrApi.approveLeave(requestId) },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['hr-leave-requests'] }) },
  })

  const rejectMutation = useMutation({
    mutationFn: async (requestId: string) => { await hrApi.rejectLeave(requestId) },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['hr-leave-requests'] }) },
  })

  const isMutating = approveMutation.isPending || rejectMutation.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Leave & Attendance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Leave requests (workforce-api) + timesheet status (last 30 days)
          </p>
        </div>
        <button disabled className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600/60 text-white rounded-lg text-sm font-medium cursor-not-allowed" title="Create leave request UI coming soon">
          <Plus className="w-4 h-4" /> New Leave Request
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Pending Leave" value={pendingLeaveCount} icon={Clock} color="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" />
        <StatCard label="On Leave Today" value={onLeaveTodayCount} icon={CalendarOff} color="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" />
        <StatCard label="Timesheets Pending" value={timesheetStats?.pending_review ?? '—'} icon={List} color="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" subtext="Review queue" />
        <StatCard label="Anomalies" value={timesheetStats?.anomalies ?? '—'} icon={AlertCircle} color="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400" subtext="Low confidence" />
      </div>

      {/* Main Tabs */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {(['leave', 'timesheets'] as const).map((tab) => (
              <button key={tab} onClick={() => setMainTab(tab)} className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                mainTab === tab ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
              )}>
                {tab === 'leave' ? 'Leave Requests' : 'Timesheets'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 w-56 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400" />
            </div>

            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {(mainTab === 'leave'
                ? (['all', 'pending', 'approved', 'rejected'] as const)
                : (['all', 'draft', 'submitted', 'approved', 'rejected'] as const)
              ).map((f) => (
                <button key={f} onClick={() => mainTab === 'leave' ? setLeaveFilter(f as LeaveStatusFilter) : setTimesheetFilter(f as TimesheetStatusFilter)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize',
                    (mainTab === 'leave' ? leaveFilter : timesheetFilter) === f
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
                  )}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {mainTab === 'leave' ? (
          <div>
            {leaveError ? <div className="p-4 text-sm text-red-600 dark:text-red-400">Failed to load leave requests</div>
              : isLeaveLoading ? <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading leave…</div>
              : <LeaveRequestsTable requests={filteredLeave} onApprove={(id) => approveMutation.mutate(id)} onReject={(id) => rejectMutation.mutate(id)} isMutating={isMutating} />}
          </div>
        ) : (
          <div>
            {timesheetsError ? <div className="p-4 text-sm text-red-600 dark:text-red-400">Failed to load timesheets</div>
              : isTimesheetsLoading ? <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading timesheets…</div>
              : <TimesheetsTable days={filteredTimesheets} />}
          </div>
        )}
      </div>
    </div>
  )
}
